import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  MessageSquare,
  Users,
  Newspaper,
  Plus,
  Send,
  Trash2,
  Search,
} from "lucide-react";
import { toast } from "sonner";
import { format, isToday, isYesterday } from "date-fns";

function timeLabel(iso) {
  const d = new Date(iso);
  if (isToday(d)) return format(d, "h:mm a");
  if (isYesterday(d)) return "Yesterday";
  return format(d, "MMM d");
}

function initials(name = "") {
  return name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

const STAFF_ROLES = ["admin", "sub_admin", "team_lead", "event_host"];

// ── Feed Tab ───────────────────────────────────────────────────────────────

function FeedTab({ user, authAxios }) {
  const [posts, setPosts] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    authAxios().get("/messages/feed")
      .then((r) => setPosts(r.data))
      .catch(() => {});
  }, [authAxios]);

  async function submit(e) {
    e.preventDefault();
    if (!body.trim()) return;
    setSending(true);
    try {
      const r = await authAxios().post("/messages/feed", { body });
      setPosts((p) => [r.data, ...p]);
      setBody("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to post");
    } finally {
      setSending(false);
    }
  }

  async function deletePost(post_id) {
    try {
      await authAxios().delete(`/messages/feed/${post_id}`);
      setPosts((p) => p.filter((x) => x.post_id !== post_id));
    } catch {
      toast.error("Could not delete post");
    }
  }

  const canDelete = (post) =>
    post.author_id === user.user_id ||
    ["admin", "sub_admin"].includes(user.role);

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto space-y-3 p-4">
        {posts.length === 0 && (
          <p className="text-center text-muted-foreground py-16 text-sm">
            No posts yet. Be the first!
          </p>
        )}
        {posts.map((p) => (
          <div key={p.post_id} className="bg-card border rounded-xl p-4 space-y-1">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Avatar className="h-7 w-7">
                  <AvatarFallback className="text-xs">{initials(p.author_name)}</AvatarFallback>
                </Avatar>
                <span className="font-medium text-sm">{p.author_name}</span>
                <Badge variant="secondary" className="text-xs capitalize">
                  {p.author_role?.replace("_", " ")}
                </Badge>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <span className="text-xs">{timeLabel(p.created_at)}</span>
                {canDelete(p) && (
                  <button
                    onClick={() => deletePost(p.post_id)}
                    className="hover:text-destructive transition-colors"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
            <p className="text-sm whitespace-pre-wrap pl-9">{p.body}</p>
          </div>
        ))}
      </div>
      <form onSubmit={submit} className="p-4 border-t flex gap-2">
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Post to the team feed…"
          className="resize-none min-h-[60px]"
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(e); }
          }}
        />
        <Button type="submit" disabled={sending || !body.trim()} size="icon" className="self-end">
          <Send className="h-4 w-4" />
        </Button>
      </form>
    </div>
  );
}

// ── New Conversation Dialog ────────────────────────────────────────────────

function NewConvDialog({ authAxios, kind, onCreated }) {
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      authAxios().get("/messages/users")
        .then((r) => setUsers(r.data))
        .catch(() => {});
      setSearch(""); setSelected([]); setGroupName("");
    }
  }, [open, authAxios]);

  const filtered = users.filter((u) =>
    u.name.toLowerCase().includes(search.toLowerCase())
  );

  function toggleUser(uid) {
    if (kind === "dm") {
      setSelected([uid]);
    } else {
      setSelected((s) =>
        s.includes(uid) ? s.filter((x) => x !== uid) : [...s, uid]
      );
    }
  }

  async function create() {
    if (selected.length === 0) return;
    if (kind === "group" && !groupName.trim()) {
      toast.error("Group name is required"); return;
    }
    setLoading(true);
    try {
      const r = await authAxios().post("/messages/conversations", {
        participant_ids: selected,
        kind,
        name: kind === "group" ? groupName.trim() : undefined,
      });
      setOpen(false);
      onCreated(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create conversation");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost" className="h-7 w-7 p-0">
          <Plus className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{kind === "dm" ? "New Direct Message" : "New Group Chat"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {kind === "group" && (
            <Input
              placeholder="Group name"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
            />
          )}
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search users…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
            />
          </div>
          <div className="max-h-56 overflow-y-auto space-y-1 border rounded-lg p-2">
            {filtered.length === 0 && (
              <p className="text-center text-muted-foreground text-sm py-4">No users found</p>
            )}
            {filtered.map((u) => (
              <button
                key={u.user_id}
                onClick={() => toggleUser(u.user_id)}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left transition-colors ${
                  selected.includes(u.user_id)
                    ? "bg-primary text-primary-foreground"
                    : "hover:bg-muted"
                }`}
              >
                <Avatar className="h-6 w-6">
                  <AvatarFallback className="text-xs">{initials(u.name)}</AvatarFallback>
                </Avatar>
                <span className="text-sm flex-1">{u.name}</span>
                <span className="text-xs opacity-60 capitalize">{u.role?.replace("_", " ")}</span>
              </button>
            ))}
          </div>
          <Button
            onClick={create}
            disabled={loading || selected.length === 0}
            className="w-full"
          >
            {loading ? "Creating…" : kind === "dm" ? "Open Chat" : "Create Group"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Conversation List Item ─────────────────────────────────────────────────

function ConvItem({ conv, active, onClick }) {
  const label =
    conv.kind === "group"
      ? conv.name || "Group Chat"
      : conv.other_name || "Direct Message";
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${
        active ? "bg-primary text-primary-foreground" : "hover:bg-muted"
      }`}
    >
      <Avatar className="h-9 w-9 shrink-0">
        <AvatarFallback className="text-sm">
          {conv.kind === "group" ? <Users className="h-4 w-4" /> : initials(label)}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-1">
          <span className="font-medium text-sm truncate">{label}</span>
          {conv.last_message_at && (
            <span className={`text-xs shrink-0 ${active ? "opacity-70" : "text-muted-foreground"}`}>
              {timeLabel(conv.last_message_at)}
            </span>
          )}
        </div>
        {conv.last_message_preview && (
          <p className={`text-xs truncate ${active ? "opacity-70" : "text-muted-foreground"}`}>
            {conv.last_message_preview}
          </p>
        )}
      </div>
    </button>
  );
}

// ── Thread View ────────────────────────────────────────────────────────────

function ThreadView({ conv, authAxios, user }) {
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const wsRef = useRef(null);

  const convId = conv?.conversation_id;

  useEffect(() => {
    if (!convId) return;
    setMessages([]);
    authAxios().get(`/messages/conversations/${convId}/messages`)
      .then((r) => setMessages(r.data))
      .catch(() => {});
  }, [convId, authAxios]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // WebSocket for real-time — use the backend WS URL derived from axios base URL
  useEffect(() => {
    if (!convId) return;
    const token = localStorage.getItem("auth_token");
    if (!token) return;
    // authAxios base URL points to the backend; convert http→ws for WS connection
    const httpBase = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    const wsBase = httpBase.replace(/^http/, "ws");
    const ws = new WebSocket(`${wsBase}/api/ws?token=${token}`);
    wsRef.current = ws;
    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.type === "new_message" && data.message?.conversation_id === convId) {
          setMessages((prev) => {
            if (prev.find((m) => m.message_id === data.message.message_id)) return prev;
            return [...prev, data.message];
          });
        }
        if (data.type === "delete_message" && data.conversation_id === convId) {
          setMessages((prev) => prev.filter((m) => m.message_id !== data.message_id));
        }
      } catch {}
    };
    return () => ws.close();
  }, [convId]);

  async function send(e) {
    e.preventDefault();
    if (!body.trim() || !convId) return;
    setSending(true);
    try {
      const r = await authAxios().post(`/messages/conversations/${convId}/messages`, { body });
      setMessages((prev) => {
        if (prev.find((m) => m.message_id === r.data.message_id)) return prev;
        return [...prev, r.data];
      });
      setBody("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  }

  async function deleteMsg(msg_id) {
    try {
      await authAxios().delete(`/messages/conversations/${convId}/messages/${msg_id}`);
      setMessages((prev) => prev.filter((m) => m.message_id !== msg_id));
    } catch {
      toast.error("Could not delete message");
    }
  }

  if (!conv) {
    return (
      <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
        Select a conversation to start chatting
      </div>
    );
  }

  const title =
    conv.kind === "group" ? conv.name || "Group Chat" : conv.other_name || "Direct Message";

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3 border-b flex items-center gap-2">
        <Avatar className="h-8 w-8">
          <AvatarFallback className="text-xs">
            {conv.kind === "group" ? <Users className="h-3.5 w-3.5" /> : initials(title)}
          </AvatarFallback>
        </Avatar>
        <div>
          <p className="font-semibold text-sm">{title}</p>
          {conv.kind === "group" && (
            <p className="text-xs text-muted-foreground">
              {(conv.participant_ids || []).length} members
            </p>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 && (
          <p className="text-center text-muted-foreground text-sm py-12">
            No messages yet. Say hello!
          </p>
        )}
        {messages.map((m) => {
          const mine = m.author_id === user.user_id;
          return (
            <div key={m.message_id} className={`flex gap-2 ${mine ? "flex-row-reverse" : ""}`}>
              {!mine && (
                <Avatar className="h-7 w-7 shrink-0 mt-0.5">
                  <AvatarFallback className="text-xs">{initials(m.author_name)}</AvatarFallback>
                </Avatar>
              )}
              <div className={`group max-w-[70%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
                {!mine && (
                  <span className="text-xs text-muted-foreground mb-0.5 ml-1">{m.author_name}</span>
                )}
                <div
                  className={`rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${
                    mine
                      ? "bg-primary text-primary-foreground rounded-tr-sm"
                      : "bg-muted rounded-tl-sm"
                  }`}
                >
                  {m.body}
                </div>
                <div className={`flex items-center gap-1 mt-0.5 ${mine ? "flex-row-reverse" : ""}`}>
                  <span className="text-xs text-muted-foreground">{timeLabel(m.created_at)}</span>
                  {(mine || ["admin", "sub_admin"].includes(user.role)) && (
                    <button
                      onClick={() => deleteMsg(m.message_id)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity hover:text-destructive"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={send} className="p-4 border-t flex gap-2">
        <Input
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Type a message…"
          className="flex-1"
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(e); }
          }}
        />
        <Button type="submit" disabled={sending || !body.trim()} size="icon">
          <Send className="h-4 w-4" />
        </Button>
      </form>
    </div>
  );
}

// ── Conversations Tab ──────────────────────────────────────────────────────

function ConvsTab({ authAxios, user, kind }) {
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);

  const load = useCallback(() => {
    authAxios().get("/messages/conversations")
      .then((r) => setConvs(r.data.filter((c) => c.kind === kind)))
      .catch(() => {});
  }, [authAxios, kind]);

  useEffect(() => { load(); }, [load]);

  function handleCreated(conv) {
    setConvs((prev) => {
      const idx = prev.findIndex((c) => c.conversation_id === conv.conversation_id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = conv;
        return next;
      }
      return [conv, ...prev];
    });
    setActive(conv);
  }

  return (
    <div className="flex h-full">
      {/* Sidebar list */}
      <div className="w-64 shrink-0 border-r flex flex-col">
        <div className="p-3 border-b flex items-center justify-between">
          <span className="text-sm font-semibold">
            {kind === "dm" ? "Direct Messages" : "Group Chats"}
          </span>
          <NewConvDialog authAxios={authAxios} kind={kind} onCreated={handleCreated} />
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
          {convs.length === 0 && (
            <p className="text-center text-muted-foreground text-xs py-8">
              {kind === "dm" ? "No DMs yet" : "No groups yet"}
            </p>
          )}
          {convs.map((c) => (
            <ConvItem
              key={c.conversation_id}
              conv={c}
              active={active?.conversation_id === c.conversation_id}
              onClick={() => setActive(c)}
            />
          ))}
        </div>
      </div>

      {/* Thread */}
      <div className="flex-1 min-w-0">
        <ThreadView conv={active} authAxios={authAxios} user={user} />
      </div>
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────

const TABS = [
  { id: "feed", label: "Team Feed", icon: Newspaper, staffOnly: true },
  { id: "dm", label: "Direct Messages", icon: MessageSquare },
  { id: "groups", label: "Groups", icon: Users },
];

export default function MessagesPage() {
  const { user, authAxios } = useAuth();
  const isStaff = STAFF_ROLES.includes(user?.role);
  const defaultTab = isStaff ? "feed" : "dm";
  const [tab, setTab] = useState(defaultTab);

  const visibleTabs = TABS.filter((t) => !t.staffOnly || isStaff);

  return (
    <div className="flex flex-col h-screen">
      {/* Header */}
      <div className="px-6 py-4 border-b">
        <h1 className="text-xl font-bold">Messages</h1>
      </div>

      {/* Tab bar */}
      <div className="flex border-b px-4 gap-1">
        {visibleTabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              tab === t.id
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <t.icon className="h-4 w-4" />
            {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {tab === "feed" && isStaff && <FeedTab authAxios={authAxios} user={user} />}
        {tab === "dm" && <ConvsTab authAxios={authAxios} user={user} kind="dm" />}
        {tab === "groups" && <ConvsTab authAxios={authAxios} user={user} kind="group" />}
      </div>
    </div>
  );
}
